import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, Grid, IconButton } from '@mui/material'
import classNames from 'classnames'
import { AddressUtils, PhoneUtils } from '@pbt/pbt-ui-components'

import Typography from '~/components/elements/Typography/Typography'

import { type Clinic, ClinicStatus } from './types/clinicTypes'

import styles from './ClinicBrief.module.scss'

export interface ClinicBriefProps {
  clinic: Clinic
  defaultExpanded?: boolean
  foldable?: boolean
}

export const ClinicBrief = ({
  clinic,
  foldable = false,
  defaultExpanded = true,
}: ClinicBriefProps) => {
  const { t } = useTranslation('TimeTable')
  const [expanded, setExpanded] = useState(defaultExpanded)

  const getStatusLabel = (status: ClinicStatus): string => {
    switch (status) {
      case ClinicStatus.VERIFIED:
        return t('TimeTable:PMR_CONSENT.CLINIC_STATUS.VERIFIED')
      case ClinicStatus.CLOSED:
        return t('TimeTable:PMR_CONSENT.CLINIC_STATUS.CLOSED')
      default:
        return t('TimeTable:PMR_CONSENT.CLINIC_STATUS.UNVERIFIED')
    }
  }
  const { address, phone } = clinic
  const formattedAddress = AddressUtils.formatAddress({
    address: address.street1 ?? undefined,
    suite: address.street2 ?? undefined,
    city: address.city ?? undefined,
    state: address.state ?? undefined,
    zip: address.postcode ?? undefined,
  })
  const phoneFormatted = phone
    ? PhoneUtils.formatPhoneNumber(PhoneUtils.parsePhoneNumber(phone))
    : null
  const email = clinic.address?.email1

  const handleToggle = () => {
    setExpanded(!expanded)
  }

  return (
    <Grid container>
      <Grid container item height="fit-content">
        {foldable && (
          <Grid item>
            <IconButton
              size="large"
              sx={{
                padding: '0 12px 2px 0',
                ...(foldable ? { cursor: 'pointer' } : {}),
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleToggle()
              }}
            >
              <ExpandMore
                className={classNames(styles.chevron, {
                  [styles.chevronExpanded]: !expanded,
                })}
              />
            </IconButton>
          </Grid>
        )}
        <Grid container item xs alignItems="center" gap={1} wrap="nowrap">
          <Grid item>
            <Typography.H3>{clinic.name}</Typography.H3>
          </Grid>
          <Grid item display="flex">
            <Box
              className={classNames(styles.badge, {
                [styles.badgeVerified]: clinic.status === ClinicStatus.VERIFIED,
                [styles.badgeUnverified]:
                  clinic.status === ClinicStatus.UNVERIFIED ||
                  (clinic.status !== ClinicStatus.VERIFIED &&
                    clinic.status !== ClinicStatus.CLOSED),
                [styles.badgeClosed]: clinic.status === ClinicStatus.CLOSED,
              })}
            >
              <Typography.Label
                className={classNames(styles.label, {
                  [styles.labelVerified]:
                    clinic.status === ClinicStatus.VERIFIED,
                  [styles.labelUnverified]:
                    clinic.status === ClinicStatus.UNVERIFIED ||
                    (clinic.status !== ClinicStatus.VERIFIED &&
                      clinic.status !== ClinicStatus.CLOSED),
                  [styles.labelClosed]: clinic.status === ClinicStatus.CLOSED,
                })}
              >
                {getStatusLabel(clinic.status)}
              </Typography.Label>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {(!foldable || expanded) && (
        <Grid
          container
          item
          direction="column"
          ml={expanded && foldable ? 4 : undefined}
          spacing={0.5}
        >
          {formattedAddress && (
            <Grid item>
              <Typography.Paragraph>{formattedAddress}</Typography.Paragraph>
            </Grid>
          )}

          {phoneFormatted && (
            <Grid item>
              <Typography.Paragraph>{phoneFormatted}</Typography.Paragraph>
            </Grid>
          )}

          {email && (
            <Grid item>
              <Typography.Paragraph>{email}</Typography.Paragraph>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default ClinicBrief
